import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Command as CommandPrimitive } from 'cmdk';
import {
    ClipboardList,
    FileText,
    Loader2,
    Package,
    Receipt,
    Search,
    Users,
    Wallet,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Dialog, DialogOverlay, DialogPortal } from '@/components/ui/dialog';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandItem,
    CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { searchCommandCenter } from '@/app/services/commandCenterService';
import { CommandCenterResult } from '@/app/types/commandCenter';
import { useDebouncedValue } from '@/app/hooks/useDebouncedValue';

interface CommandCenterProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

interface QuickAction {
    id: string;
    title: string;
    subtitle: string;
    href: string;
    icon: React.ElementType;
}

const quickActions: QuickAction[] = [
    {
        id: 'new-sales-invoice',
        title: 'New Sales Invoice',
        subtitle: 'Bill a customer for products or services',
        href: '/sales-invoices/new',
        icon: Receipt,
    },
    {
        id: 'new-quotation',
        title: 'New Quotation',
        subtitle: 'Create an estimate or quotation (sales order)',
        href: '/sales-orders/detail',
        icon: FileText,
    },
    {
        id: 'parties',
        title: 'Parties',
        subtitle: 'View customers and vendors',
        href: '/party',
        icon: Users,
    },
    {
        id: 'payments-in',
        title: 'Payments In',
        subtitle: 'Record and review received payments',
        href: '/payments-in/list',
        icon: Wallet,
    },
    {
        id: 'items',
        title: 'Items',
        subtitle: 'Browse products and services',
        href: '/items',
        icon: Package,
    },
];

const typeLabels: Record<string, string> = {
    party: 'Parties',
    voucher: 'Vouchers',
    sales_order: 'Sales Orders',
    item: 'Items',
};

const typeIcons: Record<string, React.ElementType> = {
    party: Users,
    voucher: FileText,
    sales_order: ClipboardList,
    item: Package,
};

const typeOrder = ['party', 'voucher', 'sales_order', 'item'];

export const CommandCenter: React.FC<CommandCenterProps> = ({ open, onOpenChange }) => {
    const navigate = useNavigate();
    const [query, setQuery] = useState('');
    const [selectedValue, setSelectedValue] = useState('');
    const debouncedQuery = useDebouncedValue(query.trim(), 220);

    useEffect(() => {
        if (!open) {
            setQuery('');
        }
    }, [open]);

    const { data, isFetching } = useQuery({
        queryKey: ['commandCenterSearch', debouncedQuery],
        queryFn: () => searchCommandCenter({ q: debouncedQuery, limit_per_type: 6 }),
        enabled: open && debouncedQuery.length > 0,
        staleTime: 20_000,
    });

    const results = useMemo(() => data?.items ?? [], [data]);

    const groupedResults = useMemo(() => {
        const groups = new Map<string, CommandCenterResult[]>();
        for (const result of results) {
            const bucket = groups.get(result.type) ?? [];
            bucket.push(result);
            groups.set(result.type, bucket);
        }
        return typeOrder
            .filter((type) => groups.has(type))
            .map((type) => [type, groups.get(type) ?? []] as const);
    }, [results]);

    // cmdk drops its selection when async results replace the quick actions,
    // leaving Enter a no-op until the user presses an arrow key. Re-select the
    // first visible entry whenever the rendered list changes.
    useEffect(() => {
        if (!debouncedQuery) {
            setSelectedValue(quickActions[0]?.id ?? '');
            return;
        }
        const first = groupedResults[0]?.[1][0];
        setSelectedValue(first ? `${first.type}:${first.entity_id}:${first.title}` : '');
    }, [debouncedQuery, groupedResults]);

    const runNavigation = (href: string) => {
        navigate(href);
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogPortal>
                <DialogOverlay className="bg-slate-950/45 backdrop-blur-[2px]" />
                <DialogPrimitive.Content
                    style={{
                        top: '12vh',
                        left: 0,
                        right: 0,
                        width: 'calc(100vw - 1.5rem)',
                        marginLeft: 'auto',
                        marginRight: 'auto',
                    }}
                    className={cn(
                        'fixed z-50 max-w-2xl overflow-hidden rounded-lg border bg-background shadow-2xl outline-none',
                        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95'
                    )}
                >
                    <DialogPrimitive.Title className="sr-only">Command Center</DialogPrimitive.Title>
                    <Command
                        shouldFilter={false}
                        value={selectedValue}
                        onValueChange={setSelectedValue}
                        className="rounded-none bg-background"
                    >
                        <div className="flex items-center gap-3 border-b bg-card px-4 py-3">
                            <Search className="h-5 w-5 shrink-0 text-muted-foreground" />
                            <CommandPrimitive.Input
                                autoFocus
                                value={query}
                                onValueChange={setQuery}
                                placeholder="Search parties, items, invoices, sales orders..."
                                className="h-9 min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                            />
                            {isFetching && <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />}
                            <kbd className="hidden rounded border bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground sm:inline-block">
                                ESC
                            </kbd>
                        </div>

                        <CommandList className="overflow-y-auto p-2" style={{ maxHeight: 'min(68vh, 520px)' }}>
                            {!debouncedQuery && (
                                <CommandGroup heading="Start here">
                                    {quickActions.map((action) => {
                                        const Icon = action.icon;
                                        return (
                                            <CommandItem
                                                key={action.id}
                                                value={action.id}
                                                onSelect={() => runNavigation(action.href)}
                                                className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-3"
                                            >
                                                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border bg-muted/60 text-foreground">
                                                    <Icon className="h-4 w-4" />
                                                </span>
                                                <span className="min-w-0 flex-1">
                                                    <span className="block truncate text-sm font-semibold">{action.title}</span>
                                                    <span className="block truncate text-xs text-muted-foreground">{action.subtitle}</span>
                                                </span>
                                            </CommandItem>
                                        );
                                    })}
                                </CommandGroup>
                            )}

                            {debouncedQuery && !isFetching && results.length === 0 && (
                                <CommandEmpty className="py-10 text-center text-sm text-muted-foreground">
                                    No matching records.
                                </CommandEmpty>
                            )}

                            {groupedResults.map(([type, items]) => (
                                <CommandGroup key={type} heading={typeLabels[type] ?? type}>
                                    {items.map((item) => {
                                        const Icon = typeIcons[item.type] ?? Search;
                                        return (
                                            <CommandItem
                                                key={item.id}
                                                value={`${item.type}:${item.entity_id}:${item.title}`}
                                                onSelect={() => runNavigation(item.href)}
                                                className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2.5"
                                            >
                                                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border bg-muted/60 text-foreground">
                                                    <Icon className="h-4 w-4" />
                                                </span>
                                                <span className="min-w-0 flex-1">
                                                    <span className="block truncate text-sm font-semibold">{item.title}</span>
                                                    {item.subtitle && (
                                                        <span className="block truncate text-xs text-muted-foreground">{item.subtitle}</span>
                                                    )}
                                                    {item.detail && (
                                                        <span className="block truncate text-xs text-muted-foreground/80">{item.detail}</span>
                                                    )}
                                                </span>
                                                {item.status && (
                                                    <Badge variant="outline" className="max-w-[130px] shrink-0 truncate rounded-full text-[10px] capitalize">
                                                        {String(item.status).replace(/_/g, ' ')}
                                                    </Badge>
                                                )}
                                            </CommandItem>
                                        );
                                    })}
                                </CommandGroup>
                            ))}
                        </CommandList>

                        <div className="flex items-center justify-between border-t bg-muted/30 px-4 py-2 text-xs text-muted-foreground">
                            <span>Enter opens selected record</span>
                            <span className="hidden sm:inline">Use the header shortcut from anywhere</span>
                        </div>
                    </Command>
                </DialogPrimitive.Content>
            </DialogPortal>
        </Dialog>
    );
};
